import {
  getUpscaleLimitError,
  UPSCALER_MODELS,
  type UpscaleFactor,
  type Upscaler,
} from "../utils/upscaleLimits";
import { snapshotGenerationContext } from "./backendAiService";
/**
 * Image generation engine.
 *
 * Image generation via Gemini is now handled by the backend `generateImage`
 * Cloud Function (see backendAiService.ts). This file retains:
 * - Bria background removal (already a Cloud Function)
 * - Bria product shot (already a Cloud Function)
 * - Upscale via fal — SeedVR2 / Topaz (already a Cloud Function)
 * - Inpaint style transfer (migrated to backend)
 *
 * Credit check/deduction is handled server-side by each Cloud Function,
 * NOT on the frontend.
 */

import { getCallableFunction } from "./cloudFunctionProvider.js";
import { callGenerateImage } from "./backendAiService.js";
import { trackAiRequest } from "./analyticsService.js";
import { functions } from "../config/firebase.js";
import { GeneratedContent, AppImageFile } from "../types.ts";
import { ImageFile } from "../types";
import { AppError } from "../utils/appError";

// ---------------------------------------------------------------------------
// Return type for backend operations that write to GCS
// ---------------------------------------------------------------------------

export interface GCSImageResult {
  storagePath: string;
  downloadUrl: string;
  width: number;
  height: number;
  mimeType: string;
  name: string;
  /** File size in bytes, derived by the backend via sharp. */
  sizeBytes?: number | null;
  /** True when the backend's alpha sampling classified this as transparent. */
  hasAlpha?: boolean | null;
  /** Database UUID assigned by the backend when projectId + productId were provided */
  dbId?: string | null;
}

/**
 * Converts a GCSImageResult into an AppImageFile for use in product state.
 */
export function toAppImageFile(
  result: GCSImageResult,
  id: string
): AppImageFile {
  return {
    id,
    context: "",
    base64: "",
    mimeType: result.mimeType,
    name: result.name,
    storagePath: result.storagePath,
    thumbnailUrl: result.downloadUrl,
    fullUrl: result.downloadUrl,
    width: result.width,
    height: result.height,
    sizeBytes: result.sizeBytes ?? undefined,
    hasAlpha: result.hasAlpha ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Inpaint Style Transfer constants (re-exported for callers)
// ---------------------------------------------------------------------------

export type InpaintStyleModel = "gemini-nano-banana-2.1" | "gemini-3-pro-image";

export const DEFAULT_INPAINT_STYLE_MODEL: InpaintStyleModel =
  "gemini-nano-banana-2.1";
export const INPAINT_STYLE_TEMPERATURE = 0.3;

// ---------------------------------------------------------------------------
// Inpaint Style Transfer — delegates to backend
// ---------------------------------------------------------------------------

export async function executeInpaintStyleTransfer(
  sceneImageDataUrl: string,
  stylePrompt: string,
  model: InpaintStyleModel = DEFAULT_INPAINT_STYLE_MODEL,
  relightScene?: boolean,
  sceneStoragePath?: string
): Promise<GeneratedContent> {
  const generationContext = snapshotGenerationContext();

  let resolvedPath = sceneStoragePath;

  // If no storagePath provided, upload the data URL to GCS first
  if (!resolvedPath && sceneImageDataUrl) {
    const { uploadDataUrlToStorage } = await import("../utils/imageUtils");
    resolvedPath = await uploadDataUrlToStorage(
      sceneImageDataUrl,
      "inpaint_style_transfer"
    );
  }

  if (!resolvedPath) {
    throw new AppError(
      "generation/all_providers_failed",
      "executeInpaintStyleTransfer: no sceneStoragePath and no data URL to upload."
    );
  }

  return callGenerateImage(
    "inpaint_style_transfer",
    { scene: resolvedPath },
    { stylePrompt, model, relightScene },
    generationContext
  );
}

// ---------------------------------------------------------------------------
// Background Removal via Bria RMBG 2.0 (Cloud Function)
// ---------------------------------------------------------------------------

export async function executeBackgroundRemoval(
  image: ImageFile,
  onProgress?: (message: string) => void,
  projectId?: string,
  productId?: string,
  imageType?: string,
  generationSettings?: string,
  options?: { fillBackgroundWhite?: boolean }
): Promise<GCSImageResult> {
  const startMs = Date.now();

  onProgress?.("Removing background...");

  const removeBgFn = getCallableFunction<
    {
      storagePath: string;
      projectId?: string;
      productId?: string;
      imageType?: string;
      generationSettings?: string;
      fillBackgroundWhite?: boolean;
    },
    {
      storagePath: string;
      downloadUrl: string;
      width: number | null;
      height: number | null;
      mimeType: string;
      sizeBytes?: number | null;
      hasAlpha?: boolean | null;
      dbId?: string | null;
    }
  >(functions, "removeBackgroundViaBria", { timeout: 570_000 });

  try {
    if (!image.storagePath) {
      throw new AppError(
        "generation/bg_removal_failed",
        "Image must have a storagePath for background removal."
      );
    }
    const { data } = await removeBgFn({
      storagePath: image.storagePath,
      projectId,
      productId,
      imageType,
      generationSettings,
      fillBackgroundWhite: options?.fillBackgroundWhite,
    });

    if (!data.storagePath) {
      throw new AppError(
        "generation/bg_removal_failed",
        "Background removal did not return an image."
      );
    }

    trackAiRequest({
      mode: "background_removal",
      provider: "bria-bg-remove",
      model: "fal-ai/bria/background/remove",
      success: true,
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      imageType,
      sourceImagePaths: image.storagePath ? [image.storagePath] : undefined,
    });

    return {
      storagePath: data.storagePath,
      downloadUrl: data.downloadUrl,
      width: data.width ?? 0,
      height: data.height ?? 0,
      mimeType: data.mimeType,
      sizeBytes: data.sizeBytes ?? null,
      hasAlpha: data.hasAlpha ?? null,
      name: image.name.replace(/\.[^/.]+$/, "") + "_nobg.png",
      dbId: data.dbId ?? null,
    };
  } catch (error) {
    trackAiRequest({
      mode: "background_removal",
      provider: "bria-bg-remove",
      model: "fal-ai/bria/background/remove",
      success: false,
      errorMessage: error instanceof Error ? error.message : String(error),
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      imageType,
      sourceImagePaths: image.storagePath ? [image.storagePath] : undefined,
    });

    if (error instanceof AppError) throw error;
    throw new AppError(
      "generation/bg_removal_failed",
      error instanceof Error ? error.message : String(error),
      error
    );
  }
}

// ---------------------------------------------------------------------------
// Upscale – SeedVR2 (standard) / Topaz (premium) via Cloud Function
// ---------------------------------------------------------------------------

export * from "../utils/upscaleLimits";

export async function executeUpscale(
  image: ImageFile,
  upscaleFactor: UpscaleFactor = "x2",
  upscaler: Upscaler = "seedvr",
  onProgress?: (message: string) => void,
  projectId?: string,
  productId?: string,
  imageType?: string,
  generationSettings?: string
): Promise<GCSImageResult> {
  const startMs = Date.now();

  onProgress?.("Upscaling image...");

  const upscaleFn = getCallableFunction<
    {
      storagePath: string;
      upscaleFactor: string;
      upscaler: Upscaler;
      projectId?: string;
      productId?: string;
      imageType?: string;
      generationSettings?: string;
    },
    {
      storagePath: string;
      downloadUrl: string;
      mimeType: string;
      width: number;
      height: number;
      sizeBytes?: number | null;
      hasAlpha?: boolean | null;
      dbId?: string | null;
    }
  >(functions, "upscaleImage", { timeout: 570_000 });

  try {
    if (!image.storagePath) {
      throw new AppError(
        "generation/upscale_failed",
        "Image must have a storagePath for upscaling."
      );
    }

    if (image.width && image.height) {
      const upscaleError = getUpscaleLimitError(
        image.width,
        image.height,
        upscaleFactor,
        upscaler
      );
      if (upscaleError) {
        throw new AppError(upscaleError.code, upscaleError.message);
      }
    }

    const { data } = await upscaleFn({
      storagePath: image.storagePath,
      upscaleFactor,
      upscaler,
      projectId,
      productId,
      imageType,
      generationSettings,
    });

    if (!data.storagePath) {
      throw new AppError(
        "generation/upscale_failed",
        "Upscale did not return an image."
      );
    }

    trackAiRequest({
      mode: "upscale",
      provider: "fal",
      model: UPSCALER_MODELS[upscaler],
      success: true,
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      imageType,
      sourceImagePaths: image.storagePath ? [image.storagePath] : undefined,
      upscaleFactor,
    });

    return {
      storagePath: data.storagePath,
      downloadUrl: data.downloadUrl,
      width: data.width,
      height: data.height,
      mimeType: data.mimeType || "image/png",
      sizeBytes: data.sizeBytes ?? null,
      hasAlpha: data.hasAlpha ?? null,
      name: image.name.replace(/\.[^/.]+$/, "") + `_${upscaleFactor}.png`,
      dbId: data.dbId ?? null,
    };
  } catch (error) {
    trackAiRequest({
      mode: "upscale",
      provider: "fal",
      model: UPSCALER_MODELS[upscaler],
      success: false,
      errorMessage: error instanceof Error ? error.message : String(error),
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      imageType,
      sourceImagePaths: image.storagePath ? [image.storagePath] : undefined,
      upscaleFactor,
    });

    // Backend HttpsErrors (incl. the resolution- and long-edge-limit
    // rejections, which carry `details.appCode`) are already converted to
    // AppError by the getCallableFunction wrapper (mapFirebaseError), so they
    // re-throw here.
    if (error instanceof AppError) throw error;
    throw new AppError(
      "generation/upscale_failed",
      error instanceof Error ? error.message : String(error),
      error
    );
  }
}

// ---------------------------------------------------------------------------
// Bria Product Shot – background generation via Cloud Function
// ---------------------------------------------------------------------------

const BRIA_MODEL = "fal-ai/bria/product-shot";
const BRIA_SUPPORTED_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]);

function normalizeBriaMimeType(mimeType?: string): string | null {
  if (!mimeType) return null;
  const normalized = mimeType.trim().toLowerCase();
  if (!BRIA_SUPPORTED_MIME_TYPES.has(normalized)) {
    return null;
  }
  return normalized === "image/jpg" ? "image/jpeg" : normalized;
}

/**
 * Generates a Bria Product Shot image.
 *
 * Accepts the product image as either a `storagePath` (preferred — image
 * already in GCS) or `base64Image` (for ephemeral images like the inpaint
 * composite canvas, which is assembled client-side at apply time and never
 * persisted to GCS).
 */
export async function executeBriaProductShotGeneration(
  image: { storagePath: string } | { base64Image: string },
  prompt?: string,
  refImageBase64?: string,
  refImageMimeType?: string,
  projectId?: string,
  productId?: string
): Promise<GeneratedContent> {
  const startMs = Date.now();

  const inpaintFn = getCallableFunction<
    {
      storagePath?: string;
      base64Image?: string;
      prompt?: string;
      refImageBase64?: string;
      refImageMimeType?: string;
      projectId?: string;
      productId?: string;
    },
    {
      results: Array<{
        storagePath: string;
        downloadUrl: string;
        mimeType: string;
      }>;
    }
  >(functions, "generateImageViaBriaProductShot", { timeout: 570_000 });

  try {
    const normalizedRefImageMimeType = normalizeBriaMimeType(refImageMimeType);
    if (refImageBase64 && !normalizedRefImageMimeType) {
      throw new AppError(
        "generation/all_providers_failed",
        "Unsupported Bria reference image format. Use PNG, JPG/JPEG, or WebP."
      );
    }

    const { data } = await inpaintFn({
      ...image,
      ...(normalizedRefImageMimeType
        ? { refImageMimeType: normalizedRefImageMimeType }
        : {}),
      ...(refImageBase64 ? { refImageBase64 } : { prompt }),
      projectId,
      productId,
    });

    const sourceImagePaths =
      "storagePath" in image ? [image.storagePath] : undefined;

    const first = data.results?.[0];
    if (!first) {
      throw new AppError(
        "generation/all_providers_failed",
        "Bria Product Shot did not return an image. This may be due to safety filters or an unsupported input."
      );
    }

    trackAiRequest({
      mode: "bria_product_shot",
      provider: "bria-product-shot",
      model: BRIA_MODEL,
      success: true,
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      sourceImagePaths,
    });

    return {
      id: Date.now().toString(),
      image: first.downloadUrl,
      storagePath: first.storagePath,
      thumbnailUrl: first.downloadUrl,
      text: null,
      video: null,
    };
  } catch (error) {
    const sourceImagePaths =
      "storagePath" in image ? [image.storagePath] : undefined;

    trackAiRequest({
      mode: "bria_product_shot",
      provider: "bria-product-shot",
      model: BRIA_MODEL,
      success: false,
      errorMessage: error instanceof Error ? error.message : String(error),
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      sourceImagePaths,
    });
    if (error instanceof AppError) throw error;
    throw new AppError(
      "generation/all_providers_failed",
      error instanceof Error ? error.message : String(error),
      error
    );
  }
}

// ---------------------------------------------------------------------------
// Bria Product Shot – multiple scene generation for inpaint wizard
// ---------------------------------------------------------------------------

export async function executeBriaProductShotMultiple(
  storagePath: string,
  prompt: string,
  numResults: number = 4,
  projectId?: string,
  productId?: string
): Promise<GeneratedContent[]> {
  const startMs = Date.now();

  const inpaintFn = getCallableFunction<
    {
      storagePath: string;
      prompt: string;
      numResults: number;
      projectId?: string;
      productId?: string;
    },
    {
      results: Array<{
        storagePath: string;
        downloadUrl: string;
        mimeType: string;
      }>;
    }
  >(functions, "generateImageViaBriaProductShot", { timeout: 570_000 });

  try {
    const { data } = await inpaintFn({
      storagePath,
      prompt,
      numResults,
      projectId,
      productId,
    });

    const results = data.results ?? [];
    if (results.length === 0) {
      throw new AppError(
        "generation/all_providers_failed",
        "Bria Product Shot did not return any images. This may be due to safety filters or an unsupported input."
      );
    }

    trackAiRequest({
      mode: "bria_product_shot_multi",
      provider: "bria-product-shot",
      model: BRIA_MODEL,
      success: true,
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      sourceImagePaths: [storagePath],
      numResults,
    });

    return results.map((r, i) => ({
      id: `${Date.now()}_${i}`,
      image: r.downloadUrl,
      storagePath: r.storagePath,
      thumbnailUrl: r.downloadUrl,
      text: null,
      video: null,
    }));
  } catch (error) {
    trackAiRequest({
      mode: "bria_product_shot_multi",
      provider: "bria-product-shot",
      model: BRIA_MODEL,
      success: false,
      errorMessage: error instanceof Error ? error.message : String(error),
      durationMs: Date.now() - startMs,
      projectId,
      productId,
      sourceImagePaths: [storagePath],
      numResults,
    });
    if (error instanceof AppError) throw error;
    throw new AppError(
      "generation/all_providers_failed",
      error instanceof Error ? error.message : String(error),
      error
    );
  }
}
